import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import jwt_decode from "jwt-decode";

import LOCAL_STORAGE from "@/constants/local-storage";
import { logSessionEvent } from "@/utils/session-log";
import { getAccessToken, getCustomerId, isImpersonationSession } from "@/helpers/auth-helpers";
import { IDecodedToken } from "@/interfaces/auth.interfaces";
import { isTokenExpired } from "@/utils/token-utils";
import { refreshSession } from "@/auth/token-refresh-manager";
import { markUserActivity } from "@/utils/session-activity";

const apiBaseUrl: string = import.meta.env.VITE_API_URL || "https://api-qas.talk2buy.com.br";
const apiBackofficeBaseUrl: string =
  import.meta.env.VITE_API_BACKOFFICE_URL || "https://api-backoffice-qas.talk2buy.com.br";
const apiMetaBaseUrl: string =
  import.meta.env.VITE_META_API_URL || "https://graph.facebook.com/v18.0";

const metaAccessToken = import.meta.env.VITE_META_T2B_ACCESS_TOKEN || "";

/**
 * Mantido por compatibilidade: delega para a renovação centralizada.
 */
export async function refreshAccessToken(_refreshToken?: string): Promise<string | null> {
  const outcome = await refreshSession();
  return outcome.status === "success" ? outcome.accessToken : null;
}

function isPublicRoute(pathname: string) {
  return (
    pathname.includes("/login") ||
    pathname.includes("/register") ||
    pathname.includes("/signup") ||
    pathname.includes("/terms") ||
    pathname.includes("/privacy")
  );
}

function forceLogout(reason = "desconhecido") {
  // Em rota pública não há sessão a encerrar: evita loop de logout/redirect.
  if (isPublicRoute(window.location.pathname)) return;

  const hadSession =
    Boolean(localStorage.getItem(LOCAL_STORAGE.accessTokenKey)) ||
    Boolean(localStorage.getItem(LOCAL_STORAGE.refreshTokenKey));

  // Sem tokens não há sessão para encerrar: evita loop de logout em rota pública.
  if (!hadSession) return;

  logSessionEvent(`forceLogout: ${reason}`);
  localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
  localStorage.removeItem(LOCAL_STORAGE.refreshTokenKey);
  window.dispatchEvent(new CustomEvent("tokenExpired"));

  window.location.href = "/login";
}



export const apiNext = axios.create({
  baseURL: "/api",
});

apiNext.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    const customerId = getCustomerId();

    if (accessToken && customerId) {
      config.headers.accessToken = accessToken;
      config.headers.CustomerId = customerId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiNext.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => Promise.reject(error)
);

export const apiT2B = axios.create({
  baseURL: apiBaseUrl,
});

apiT2B.interceptors.request.use(
  async (config) => {
    const isAuthRoute =
      config.url?.includes("/login") ||
      config.url?.includes("/register") ||
      config.url?.includes("/signup");
    const isInternalLogin = config.data?.login && config.data?.password;
    const isRecaptchaRoute = config.url?.includes("/recaptcha");

    if ((isAuthRoute && !isInternalLogin) || isRecaptchaRoute) {
      return config;
    }

    if (config.headers && (config.headers as Record<string, unknown>).Authorization) {
      return config;
    }

    let accessToken = getAccessToken();
    const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

    if (!accessToken) {
      if (refreshToken && !isAuthRoute) {
        const outcome = await refreshSession();
        if (outcome.status === "success") {
          accessToken = outcome.accessToken;
        } else {
          if (outcome.status === "invalid") {
            forceLogout(`request: refresh inválido (${outcome.reason})`);
          }
          return Promise.reject(new AxiosError("No authentication token", undefined, config));
        }
      } else if (!isAuthRoute) {
        forceLogout("request: sem accessToken e sem refreshToken");
        return Promise.reject(new AxiosError("No authentication token", undefined, config));
      } else {
        return config;
      }
    }

    try {
      let decodedToken: IDecodedToken = jwt_decode(accessToken);
      const isImpersonation = isImpersonationSession(decodedToken);

      // Impersonation também renova via /refreshtoken enquanto a sessão estiver válida.
      if (isTokenExpired(decodedToken)) {
        if (refreshToken) {
          const outcome = await refreshSession();
          if (outcome.status === "success") {
            accessToken = outcome.accessToken;
            decodedToken = jwt_decode(outcome.accessToken);
          } else if (outcome.status === "invalid") {
            forceLogout(
              isImpersonation
                ? `request: impersonation expirada (${outcome.reason})`
                : `request: refresh inválido (${outcome.reason})`
            );
            return Promise.reject(new AxiosError("Session expired", undefined, config));
          }
        } else if (isImpersonation) {
          forceLogout("request: impersonation expirada");
          return Promise.reject(new AxiosError("Impersonation session expired", undefined, config));
        }
      }

      if (!isTokenExpired(decodedToken) && accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        config.headers.CustomerId = decodedToken.CustomerId || "";
      }
    } catch {
      // Não forçar logout em decode inválido — alinhado ao Backoffice / http-client
      return Promise.reject(new AxiosError("Invalid token", undefined, config));
    }


    return config;
  },
  (error) => Promise.reject(error)
);

apiT2B.interceptors.response.use(
  (response) => {
    markUserActivity();
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes("/refreshtoken")) {
      forceLogout("response: 401 na chamada de refreshtoken");
      return Promise.reject(error);
    }

    // Impersonation com JWT ainda válido: 401 é permissão, não sessão.
    // Token expirado segue para refresh (sliding session).
    try {
      const accessToken = getAccessToken();
      if (accessToken) {
        const decodedToken: IDecodedToken = jwt_decode(accessToken);
        if (isImpersonationSession(decodedToken) && !isTokenExpired(decodedToken)) {
          return Promise.reject(error);
        }
      }
    } catch {
      // Decode inválido: segue para fluxo de refresh / logout
    }

    originalRequest._retry = true;

    const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

    if (!refreshToken) {
      forceLogout("response: 401 sem refreshToken");
      return Promise.reject(error);
    }

    const outcome = await refreshSession();

    if (outcome.status !== "success") {
      if (outcome.status === "invalid") {
        forceLogout(`response: refresh inválido apos 401 (${outcome.reason})`);
      }
      return Promise.reject(error);
    }

    if (originalRequest.headers) {
      originalRequest.headers.Authorization = `Bearer ${outcome.accessToken}`;
    }

    return apiT2B(originalRequest);
  }

);

export const apiBackofficeT2b = axios.create({
  baseURL: apiBackofficeBaseUrl,
});

export const apiMeta = axios.create({
  baseURL: apiMetaBaseUrl,
  headers: {
    Authorization: `Bearer ${metaAccessToken}`,
  },
});

apiMeta.interceptors.request.use(
  (config) => {
    config.headers.Authorization = `Bearer ${metaAccessToken}`;
    return config;
  },
  (error) => Promise.reject(error)
);

export const apiGupShup = axios.create({
  baseURL: "https://partner.gupshup.io/partner",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  },
});
